import type paper from "paper";
import { newId } from "../model/ids";
import {
  createPiece,
  isVisible,
  nextCutoutName,
  nextPartName,
  nextStitchName,
  projectConstructionLines,
  resolvePartStitchingDefaults,
  type ConstructionOutline,
  type Cutout,
  type Project,
  type StitchLine,
  type Vec2,
} from "../model/Project";
import { withPiece, withStitchLine } from "../model/projectOps";
import {
  anchorSingleLineStitchOutline,
  buildConstructionOutlinePath,
  buildConstructionOutlinePoints,
  clipStitchOutlineToPart,
} from "../model/constructionOutline";
import { isFeatureEnabled } from "../featureFlags";
import { newSeamLayoutDefaults } from "../model/stitchLayout";
import { zeroFollowerInsetsAtClippedEnds } from "../model/constructionStitchGrid";
import type { EditorContext } from "./EditorContext";

/** What a Trace / Region tool creates from a construction outline (#653). */
export type ShapeMode = "leather" | "cutout" | "stitch";

/** A point counts as "on" a part's leather if it lands ON the edge to within
 *  this tolerance (project mm). A seam traced along a shared construction edge
 *  sits exactly on the parts' boundaries, where Paper's strict `contains()` is
 *  false; this slack lets those parts claim the seam. Kept tiny so a part the
 *  seam merely passes near (mm+ away) is never caught. */
const EDGE_TOL = 0.5;

/** True when `p` lies ON a piece's leather — strictly inside, or within
 *  `EDGE_TOL` of its edge (so a point snapped to a shared construction border
 *  still counts). */
function pointOnPiece(path: paper.PathItem, p: paper.Point): boolean {
  return (
    path.contains(p) ||
    (path.getNearestLocation(p)?.point.getDistance(p) ?? Infinity) <= EDGE_TOL
  );
}

/** True when `pt` (project mm) is on the leather of ANY visible piece (#703).
 *  Used by the Trace tool to reject cutout / stitch vertices picked off the
 *  leather — those shapes must live inside a piece. */
export function isPointOnLeather(
  project: Project,
  scope: paper.PaperScope,
  pt: Vec2,
): boolean {
  const p = new scope.Point(pt.x, pt.y);
  for (const piece of project.pieces) {
    if (!isVisible(piece)) continue;
    let path: paper.PathItem;
    try {
      path = new scope.CompoundPath({
        pathData: piece.shapePathData,
        insert: false,
      });
    } catch {
      continue;
    }
    const hit = pointOnPiece(path, p);
    path.remove();
    if (hit) return true;
  }
  return false;
}

/** Every visible piece a traced outline actually lands ON: those whose leather
 *  holds it. Empty when nothing holds it — a cutout / stitch traced entirely off
 *  the leather has no parent and is rejected (#703), never dropped onto a part
 *  it merely shares a bounding box with. Ordered by project order. A single
 *  traced stitch fans out to ALL of these (one seam per part sharing the
 *  construction edge); a cutout / leather takes just the first.
 *
 *  A part "holds" the outline when its centroid is inside it (the usual case —
 *  a region dropped into a part) OR ANY part of any segment lies on it (sampled
 *  along the segment, inside or within `EDGE_TOL` of the edge). Sampling along —
 *  not just at the vertices — is what lets ONE trace land on several parts: a
 *  segment that runs along part A's border, or crosses from part A into part B
 *  mid-segment, is caught for each part whose leather any stretch of it touches. */
function findParentPieces(
  project: Project,
  scope: paper.PaperScope,
  points: Vec2[],
): string[] {
  if (points.length === 0) return [];
  const pts = points.map((p) => new scope.Point(p.x, p.y));
  const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
  const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
  const centroid = new scope.Point(cx, cy);
  const containing: string[] = [];
  for (const piece of project.pieces) {
    // Skip hidden parts so the user can hide the part on top and drop the new
    // cutout / stitch onto the one beneath it.
    if (!isVisible(piece)) continue;
    let path: paper.PathItem;
    try {
      path = new scope.CompoundPath({
        pathData: piece.shapePathData,
        insert: false,
      });
    } catch {
      continue;
    }
    // Walk each segment, sampling ~1mm apart (min 4 per segment): the part holds
    // the seam if ANY sample lands on it, so even a segment that only partly
    // overlaps the part — or crosses a shared border mid-span — still counts.
    let touches = false;
    for (let i = 0; i + 1 < pts.length && !touches; i++) {
      const a = pts[i]!;
      const b = pts[i + 1]!;
      const n = Math.max(4, Math.min(64, Math.round(a.getDistance(b))));
      for (let k = 0; k <= n; k++) {
        if (pointOnPiece(path, a.add(b.subtract(a).multiply(k / n)))) {
          touches = true;
          break;
        }
      }
    }
    const holds = path.contains(centroid) || touches;
    path.remove();
    if (holds) containing.push(piece.id);
  }
  // A seam along a shared edge is held by every part it joins — return them all.
  return containing;
}

/** Commit a construction outline as the entity for the active shape mode — a
 *  leather piece, or a cutout / stitch line inside its parent piece (all linked
 *  to the construction lines, so they re-derive). Returns false (no commit) if
 *  the geometry can't be built, or a cutout/stitch has no parent piece. */
export function commitConstructionShape(
  ctx: EditorContext,
  outline: ConstructionOutline,
  mode: ShapeMode,
): boolean {
  const scope = ctx.scene.getPaperScope();
  const project = ctx.history.state;
  const lines = projectConstructionLines(project);

  // The magnetic LINK back to the construction lines (re-derive on every edit,
  // plus the trace connection chips) is gated behind the
  // `construction-connections` flag. With it off the tools still create
  // geometry, but BAKED: a plain piece / cutout / stitch with no
  // `constructionOutline`, so it isn't attached to the lines.
  const link = isFeatureEnabled("construction-connections");

  if (mode === "leather") {
    const path = buildConstructionOutlinePath(outline, lines, scope);
    if (!path) return false;
    const piece = {
      ...createPiece(nextPartName(project), path),
      ...(link ? { constructionOutline: outline } : {}),
    };
    ctx.commit("Create part from construction", (proj) =>
      withPiece(proj, piece),
    );
    ctx.setSelection({ pieceId: piece.id, decorationId: null });
    return true;
  }

  // Cutout / stitch need geometry and a parent piece to live in.
  const points = buildConstructionOutlinePoints(outline, lines, scope);
  if (!points || points.length < 2) return false;
  const parentIds = findParentPieces(project, scope, points);
  const parentId = parentIds[0];
  if (!parentId) return false;
  const parent = project.pieces.find((p) => p.id === parentId);
  if (!parent) return false;

  if (mode === "cutout") {
    const path = buildConstructionOutlinePath(outline, lines, scope);
    if (!path) return false;
    const cutout: Cutout = {
      id: newId("cut"),
      name: nextCutoutName(parent),
      pathData: path,
      ...(link ? { constructionOutline: outline } : {}),
    };
    ctx.commit("Cut from construction", (proj) => ({
      ...proj,
      pieces: proj.pieces.map((p) =>
        p.id === parentId ? { ...p, cutouts: [...p.cutouts, cutout] } : p,
      ),
    }));
    ctx.setSelection({
      pieceId: parentId,
      cutoutId: cutout.id,
      decorationId: null,
    });
    return true;
  }

  // Stitch line — points come straight from the outline; the chisel params
  // resolve from each parent piece's stitching defaults. A construction edge can
  // be shared by several visible parts (e.g. two panels sewn together along the
  // same seam), so we drop one seam onto EVERY part that contains the outline.
  // They all trace the same construction line, so Phase 3's grid auto-aligns
  // them hole-for-hole — drawing once is the same as drawing each by hand.
  // Each part's seam is CLIPPED to the span(s) on that part's leather, so a
  // bigger part gets more holes than a smaller one (overlapping parts of
  // different sizes) instead of every part cloning the full-length seam. A part
  // the trace leaves and re-enters (a notch / slot) gets a SEPARATE seam per
  // on-part run, so the row doesn't bridge the gap.
  // Number the new seams consecutively from the project-wide max — they're
  // created in one commit, so each takes the next global "Stitch N" in turn.
  // Layer B (#901): before clipping, anchor a single-line trace's endpoints to
  // the line it traces (not to a crossing with a bystander line) so the LEAD
  // (whole line) and each clipped FOLLOWER track the line together when it moves.
  const stitchOutline = link
    ? anchorSingleLineStitchOutline(outline, lines, scope)
    : outline;
  let seamNo = 0;
  const seams = parentIds.flatMap((pid) => {
    const part = project.pieces.find((p) => p.id === pid);
    if (!part) return [];
    const eff = resolvePartStitchingDefaults(project, part);
    // Trim the outline to the on-part run(s). Only when linked — a baked seam
    // keeps no outline, and the grid is off then anyway. Falls back to the full
    // seam if clipping finds nothing (e.g. a seam sitting exactly on the edge).
    const clippedOutlines = link
      ? clipStitchOutlineToPart(stitchOutline, lines, part.shapePathData, scope)
      : [stitchOutline];
    const list = clippedOutlines.length > 0 ? clippedOutlines : [stitchOutline];
    return list.flatMap((co) => {
      const seamPoints =
        co === stitchOutline
          ? points
          : (buildConstructionOutlinePoints(co, lines, scope) ?? points);
      if (seamPoints.length < 2) return [];
      const line: StitchLine = {
        id: newId("stitch"),
        name: nextStitchName(project, seamNo++),
        points: seamPoints,
        pitch: eff.pitch,
        size: eff.size,
        shape: eff.shape,
        slantAngle: eff.slantAngle,
        pitchMode: eff.pitchMode,
        startOffset: 0,
        overridesDefaults: false,
        // A closed loop clipped to a partial arc comes back open.
        closed: co.closed || undefined,
        ...newSeamLayoutDefaults(),
        ...(link ? { constructionOutline: co } : {}),
      };
      return [{ pieceId: pid, line }];
    });
  });
  if (seams.length === 0) return false;
  const label =
    seams.length > 1
      ? `Stitch ${seams.length} parts from construction`
      : "Stitch from construction";
  const newIds = new Set(seams.map((s) => s.line.id));
  const gridOn =
    link &&
    isFeatureEnabled("stitch-matching") &&
    isFeatureEnabled("construction-connections");
  ctx.commit(label, (proj) => {
    const added = seams.reduce(
      (acc, s) => withStitchLine(acc, s.pieceId, s.line),
      proj,
    );
    // A new follower clipped to its part mid-row starts its working area right
    // at that edge, not a default inset in from it (#1106).
    return gridOn
      ? zeroFollowerInsetsAtClippedEnds(added, newIds, scope)
      : added;
  });
  // Select the first new seam (its part is the topmost in project order).
  const first = seams[0]!;
  ctx.setSelection({
    pieceId: first.pieceId,
    stitchId: first.line.id,
    decorationId: null,
  });
  return true;
}
